import { computed, reactive, ref, watch } from 'vue';
import { isEqual } from 'lodash-es';
import type { PermissionsUser } from '@/types/permissions';
import type {
  GrantKey,
  PermissionDraftMap,
  PermissionHelperKind,
  PermissionModuleId,
  PermissionRow,
} from '@/types/permissionModules';
import {
  PERMISSION_MODULES,
  fullAccessDraft,
  helperRows,
  isEditAllGranted,
  isViewLockedByEdit,
  pairedViewKey,
} from '@/utils/UserPermissions';

export interface OpenPermissionsEditDrawerOptions {
  /**
   * `false` keeps the drawer editable. Used by the temporary dev permissions mock,
   * which opens the current user. Omit it and the admin read-only rules apply.
   */
  readOnly?: boolean;
  /**
   * Temporary dev mock (GM-3801). Skips the grants request, seeds `draft` locally,
   * and Save writes that draft onto the permissions store. Cleared when the drawer closes.
   */
  useMockPermissions?: boolean;
  draft?: PermissionDraftMap;
}

export interface UsePermissionsEditDrawerState {
  /** The full selection, Super Admins included. Bulk mode and exclusion are derived from it. */
  users: PermissionsUser[];
  isOpen: boolean;
  draftPermissions: PermissionDraftMap;
  /** `null` defers to the admin read-only rules. */
  readOnly: boolean | null;
  /** Temporary dev mock (GM-3801). Skips the grants request and Save writes the local override. */
  useMockPermissions: boolean;
}

const DEFAULT_STATE: UsePermissionsEditDrawerState = {
  isOpen: false,
  users: [],
  draftPermissions: {},
  readOnly: null,
  useMockPermissions: false,
};

const getDefaultState = (): UsePermissionsEditDrawerState => ({
  ...DEFAULT_STATE,
  users: [],
  draftPermissions: {},
});

const state = reactive<UsePermissionsEditDrawerState>(getDefaultState());
const initialPermissionsMap = reactive<PermissionDraftMap>({});
const isRendered = ref(false);

/** Bulk edit starts from a selection of two or more users, however many remain editable. */
const isBulk = computed(() => state.users.length > 1);

/**
 * The users a save would target. Super Admins hold every permission by role and can store no
 * grants, so a bulk selection drops them. A lone Super Admin stays for the read-only view.
 */
const editableUsers = computed(() =>
  isBulk.value ? state.users.filter((user) => !user.isSuperAdmin) : state.users,
);

/** Super Admins dropped from a bulk selection, for the drawer warning. */
const excludedSuperAdminCount = computed(() => state.users.length - editableUsers.value.length);

/** The single subject of a non-bulk open. Bulk mode has no single subject. */
const singleUser = computed(() => (isBulk.value ? null : (state.users[0] ?? null)));

const hasChanges = computed(() => !isEqual(state.draftPermissions, initialPermissionsMap));

function clearPermissionMaps() {
  (Object.keys(initialPermissionsMap) as GrantKey[]).forEach((key) => {
    delete initialPermissionsMap[key];
  });
  (Object.keys(state.draftPermissions) as GrantKey[]).forEach((key) => {
    delete state.draftPermissions[key];
  });
}

/**
 * Roster VIEW is derived, so a stored row for it would make `hasChanges` dirty immediately.
 * An EDIT without its paired VIEW would show a locked, unchecked VIEW. Both copies must be
 * this same set, or the drawer opens already dirty.
 */
function normalizeLoadedPermissions(permissions: PermissionDraftMap): PermissionDraftMap {
  const normalized: PermissionDraftMap = {};

  (Object.keys(permissions) as GrantKey[]).forEach((key) => {
    if (key === 'viewRoster' || !permissions[key]) return;

    normalized[key] = true;
    const viewKey = pairedViewKey(key);

    if (viewKey) {
      normalized[viewKey] = true;
    }
  });

  return normalized;
}

function replaceLoadedPermissions(permissions: PermissionDraftMap) {
  const normalized = normalizeLoadedPermissions(permissions);

  clearPermissionMaps();
  Object.assign(initialPermissionsMap, normalized);
  Object.assign(state.draftPermissions, normalized);
}

function resetState() {
  clearPermissionMaps();
  state.isOpen = false;
  state.users = [];
  state.readOnly = null;
  state.useMockPermissions = false;
}

watch(
  () => state.isOpen,
  (isOpen) => {
    if (isOpen && !isRendered.value) {
      isRendered.value = true;
    }
    if (!isOpen) {
      clearPermissionMaps();
      state.users = [];
      state.readOnly = null;
      state.useMockPermissions = false;
    }
  },
  { flush: 'sync' },
);

export function usePermissionsEditDrawer() {
  /**
   * Opens the drawer for the full selection, Super Admins included. A lone Super Admin opens
   * read-only with every permission granted: Super Admins hold all permissions by role and
   * store no grants.
   *
   * `options` serves the temporary dev permissions mock (GM-3801): it forces an editable
   * drawer, seeds a local draft, and makes Save write that draft onto the permissions store.
   */
  function open(users: PermissionsUser[], options?: OpenPermissionsEditDrawerOptions) {
    state.users = [...users];
    state.readOnly = options?.readOnly ?? null;
    state.useMockPermissions = options?.useMockPermissions === true;
    const single = users.length === 1 ? users[0] : null;
    replaceLoadedPermissions(options?.draft ?? (single?.isSuperAdmin ? fullAccessDraft() : {}));
    state.isOpen = true;
  }

  /**
   * Sets or clears a single grant row in the draft.
   * The draft is sparse: only granted keys are stored, so unchecking deletes the key. This keeps
   * check-then-uncheck equal to the loaded map, which `hasChanges` (`isEqual`) relies on.
   *
   * Checking EDIT also checks the paired VIEW. Clearing EDIT leaves that VIEW in place.
   * A VIEW cannot be cleared while its EDIT is still granted. Roster VIEW is derived for display
   * and is never stored.
   */
  function toggleGrant(key: GrantKey, value: boolean) {
    if (key === 'viewRoster') {
      return;
    }

    if (!value && isViewLockedByEdit(state.draftPermissions, key)) {
      return;
    }

    if (value) {
      state.draftPermissions[key] = true;
      const viewKey = pairedViewKey(key);

      if (viewKey) {
        state.draftPermissions[viewKey] = true;
      }

      // Reaching the final EDIT grant implies every VIEW grant (EDIT implies VIEW), including
      // VIEW-only rows such as Earnings Benchmarks that have no EDIT pair to pull them in.
      if (isEditAllGranted(state.draftPermissions)) {
        helperRows('selectAllView').forEach((viewRow) => {
          state.draftPermissions[viewRow.key] = true;
        });
      }
    } else {
      delete state.draftPermissions[key];
    }
  }

  /**
   * Applies a Full Access helper (the "view all" and "edit all" rows) across the toggleable rows of that mode.
   *
   * Turning "Can edit all areas" on grants the VIEW-only areas (Earnings Benchmarks) too: the
   * final EDIT row flips `isEditAllGranted` inside `toggleGrant`, which then applies every VIEW
   * row. Turning it off leaves every VIEW row in place, matching the single EDIT/VIEW pair.
   */
  function toggleHelper(helper: PermissionHelperKind, value: boolean) {
    helperRows(helper).forEach((row) => toggleGrant(row.key, value));
  }

  /**
   * Grant edits before views when turning a module off, so a paired VIEW is no longer locked
   * by the time it is cleared. Grant edits before views when turning a module on, so EDIT
   * implies VIEW before the VIEW row is applied.
   */
  function rowsInMutationOrder(rows: PermissionRow[], value: boolean): PermissionRow[] {
    const rank = (row: PermissionRow) => {
      if (!value) {
        if (row.kind === 'helper') return row.helper === 'selectAllEdit' ? 0 : 2;
        return row.mode === 'edit' ? 1 : 3;
      }

      if (row.kind === 'helper') return 0;
      return row.mode === 'edit' ? 1 : 2;
    };

    return [...rows].sort((a, b) => rank(a) - rank(b));
  }

  /** Applies a module header toggle across that module's toggleable rows. Unknown ids are ignored. */
  function toggleModule(moduleId: PermissionModuleId, value: boolean) {
    const permissionModule = PERMISSION_MODULES.find((candidate) => candidate.id === moduleId);

    rowsInMutationOrder(permissionModule?.rows ?? [], value).forEach((row) => {
      if (row.kind === 'helper') {
        toggleHelper(row.helper, value);
      } else if (row.editable) {
        toggleGrant(row.key, value);
      }
    });
  }

  return {
    state,
    isRendered,
    isBulk,
    editableUsers,
    excludedSuperAdminCount,
    singleUser,
    hasChanges,
    initialPermissionsMap,
    resetState,
    open,
    replaceLoadedPermissions,
    toggleGrant,
    toggleHelper,
    toggleModule,
  };
}
